import * as React from 'react'
import { ArrayUtils, getMaskedNaverId, isDev } from '@shopping/common'
import { THUMB_TYPE } from '@shopping/Img'
import OrderServiceType from 'common/constants/order/orderServiceType'
import ProductOrderStatusType from 'common/constants/order/productOrderStatusType'
import SaleChannelType from 'common/constants/order/saleChannelType'
import ProductClassType from 'common/constants/order/productClassType'
import { ImageInspectionType } from 'common/constants/review'
import { ReviewsFilterState } from 'common/types/entities/review'
import { ProductOrderBenefitInfo } from 'common/types/entities/api/order'
import { Product } from 'common/types/entities/api/product'
import { HtmlUtils } from 'common/utils/htmlUtils'
import { NaverProfileImage } from 'common/types/entities/api/naverProfileImage'
import { SubVertical } from 'common/constants'
import { ReviewAttachStatusType } from 'common/constants/review/statusType'

const DEFAULT_PROFILE_IMG_URL = 'https://profile-phinf.pstatic.net/404/default.png'

export interface Review {
  bestReview: boolean
  bestReviewSelectDate: number
  channelId: number
  channelServiceType: ChannelServiceType
  checkoutMerchantId: string
  checkoutMerchantNo: number
  purchaseDecisionYmdt?: number
  createDate: number
  afterUseReviewCreateYmdt?: string
  detailCategorizeCategoryId: string
  freeTrialReview: boolean
  helpCount: number
  id: number
  knowledgeShoppingMallProductId: string
  largeCategorizeCategoryId: string
  middleCategorizeCategoryId: string
  modifyDate: string
  orderNo: string
  parentReviewSeq: number
  productName: string
  productNo: string
  productOptionContent: string
  productOrderNo: string
  productUrl: string
  productImageUrl: string
  merchantChannelServiceType: string
  merchantServiceType: string
  merchantChannelUrl: string
  merchantChannelName: string
  merchantUrl: string
  merchantChannelNo: string
  merchantNo: string
  merchantName: string
  report: boolean
  reportDate: string
  repurchase: boolean
  reviewAttaches: ReviewAttachment[]
  reviewComments: ReviewComment[]
  reviewContent: string
  reviewContentClassType: ReviewContentClassType
  reviewDisplayStatusType: StatusType
  reviewScore: number
  reviewServiceType: ReviewServiceType
  reviewTopics: ReviewTopic[]
  reviewType: ReviewType
  smallCategorizeCategoryId: string
  writerMemberId: string
  writerMemberIdNo: string
  writerMemberNo: number
  writerMemberProfileImageUrl: string
  isMyReview?: boolean
  prevReview?: Review
  isMyLike?: boolean
  productPcDiscountPrice?: number
  productMobileDiscountPrice?: number
  benefitPaymentDate?: number
  registered?: boolean
  modified?: boolean
}

export interface PagedReviews {
  contents: Review[]
  page: number
  size: number
  totalElements: number
  totalPages: number
  loaded?: boolean
  reviewsFilter?: ReviewsFilterState
}

export interface AdditionalPayInfo {
  productOrders: {
    merchantChannelServiceType: string
    merchantChannelUrl: string
    purchaseDecisionYmdt: string
    productOrderId: string
    merchantChannelName: string
    merchantUrl: string
    merchantChannelNo: string
    merchantNo: string
    merchantName: string
  }[]
  reviews: {
    reviewContentType: string
    afterUseReviewCreateYmdt: string
    afterUseReviewContentType: string
    reviewCreateYmdt: string
  }[]
  productOrderBenefitInfos: ProductOrderBenefitInfo[]
}

export enum ChannelServiceType {
  STOREFARM = 'STOREFARM',
  WINDOW = 'WINDOW',
  AFFILIATE = 'AFFILIATE',
}

export interface ReviewAttachmentForm {
  attachDirectoryName?: string // 디렉토리명
  attachHeight: number // 높이
  attachName: string // 첨부명
  attachPath: string // 경로
  attachSize: number // 크기
  attachVid: string // vid
  attachWidth: number // 너비
  mimeTypeName: string // MIME 타입
  reviewAttachmentType: ReviewAttachmentType // 구분코드
  reviewAttachStatusType?: ReviewAttachStatusType // 블라인드 여부
}
export interface ReviewAttachment extends ReviewAttachmentForm {
  attachCount: number
  attachInKey: string
  attachServiceId: number
  id: number
  reviewId: number
}

export enum ReviewAttachmentType {
  I = 'I', // 이미지
  M = 'M', // 동영상
}

export interface ReviewComment {
  commentContent: string
  createDate: string
  reviewCommentId: string
  reviewCommentDisplayStatusType: StatusType
  reviewId: number
  reviewServiceType: ReviewServiceType
  writerMerchantId: string
  writerMerchantNo: number
}

/*
PHOTO X, VIDEO X => TEXT
PHOTO O => PHOTO
VIDEO O => VIDEO
PHOTO O & VIDEO O => VIDEO
 */
export enum ReviewContentClassType {
  TEXT = 'TEXT',
  PHOTO = 'PHOTO',
  VIDEO = 'VIDEO ',
}

export enum ReviewServiceType {
  CHECKOUT = 'CHECKOUT',
  SELLBLOG = 'SELLBLOG',
}

export enum ReviewType {
  NORMAL = 'NORMAL',
  AFTER_USE = 'AFTER_USE',
}

export enum ReviewSortType {
  REVIEW_CREATE_DATE_DESC = 'REVIEW_CREATE_DATE_DESC',
  REVIEW_CREATE_DATE_ASC = 'REVIEW_CREATE_DATE_ASC',
  PRODUCT_NAME = 'PRODUCT_NAME',
  REVIEW_SCORE_DESC = 'REVIEW_SCORE_DESC',
  REVIEW_SCORE_ASC = 'REVIEW_SCORE_ASC',
  BEST_REVIEW_SELECTED_DATE = 'BEST_REVIEW_SELECTED_DATE',
  PRODUCT_ORDER_NO = 'PRODUCT_ORDER_NO',
  REVIEW_RANKING = 'REVIEW_RANKING',
}

export interface ReviewIdsParam {
  merchantNo: string
  originProductNo: string
  pageSize?: number // MAX 100
  reviewContentClassTypes?: ReviewContentClassType[]
  reviewServiceType?: ReviewServiceType
  sortType?: ReviewSortType
}

export interface ReviewEvaluationResult {
  personalizeReviewEvaluationVOs?: ReviewEvaluation[] // 스스에서 추후 설정 예정
  productReviewEvaluationVOs: ReviewEvaluation[]
  reviewTopicVOs: ReviewTopic[]
}

export interface ReviewEvaluation {
  reviewEvaluationCalculateValueVO: ReviewEvaluationCalculate
  reviewEvaluationName: string
  reviewEvaluationPhrase?: string
  reviewEvaluationSeq: number
  reviewEvaluationValues: ReviewEvaluationDetail[]
  sectionUseYn: boolean
}

export interface ReviewEvaluationCalculate {
  maxCountOfEvaluationValueSeq: number
  totalCountOfEvaluationValues: number
}

export interface ReviewEvaluationDetail {
  count: number
  percent: number
  reviewEvaluationValueName: string
  reviewEvaluationValueSeq: number
  sectionMaxValue?: number
  sectionMinValue?: number
  sortOrder: number
}

export interface ReviewTopic {
  topicCode: string
  topicCodeName: string
  patternEndNo?: number
  patternStartNo?: number
  endIdx?: number
  startIdx?: number
  invalidTopic?: boolean
}

export enum ReviewSearchSortType {
  REVIEW_CREATE_DATE_DESC = 'REVIEW_CREATE_DATE_DESC',
  REVIEW_CREATE_DATE_ASC = 'REVIEW_CREATE_DATE_ASC',
  PRODUCT_NAME = 'PRODUCT_NAME',
  REVIEW_SCORE_DESC = 'REVIEW_SCORE_DESC',
  REVIEW_SCORE_ASC = 'REVIEW_SCORE_ASC',
  BEST_REVIEW_SELECTED_DATE = 'BEST_REVIEW_SELECTED_DATE',
  PRODUCT_ORDER_NO = 'PRODUCT_ORDER_NO',
  REVIEW_RANKING = 'REVIEW_RANKING',
}

type ReviewSearchSortConfig = {
  [key in ReviewSearchSortType]?: {
    name: string
    nClickCode: string
  }
}

export const ReviewSearchSort: ReviewSearchSortConfig = {
  REVIEW_RANKING: { name: '랭킹순', nClickCode: 'srank' },
  REVIEW_CREATE_DATE_DESC: { name: '최신순', nClickCode: 'srecent' },
  REVIEW_SCORE_DESC: { name: '평점 높은순', nClickCode: 'sevalup' },
  REVIEW_SCORE_ASC: { name: '평점 낮은순', nClickCode: 'sevaldn' },
}

export enum ReviewSearchFeatureType {
  ALL = 'ALL',
  PHOTO_VIDEO = 'PHOTO_VIDEO',
  BEST = 'BEST',
  AFTER_USE = 'AFTER_USE',
}

type ReviewSearchFeatureConfig = {
  [key in ReviewSearchFeatureType]?: {
    name: string
    nClickCode: string
  }
}

export const ReviewSearchFeature: ReviewSearchFeatureConfig = {
  ALL: { name: '전체보기', nClickCode: 'oall' },
  PHOTO_VIDEO: { name: '포토/동영상', nClickCode: 'optvid' },
  BEST: { name: '스토어PICK', nClickCode: 'ospick' },
  AFTER_USE: { name: '한달사용리뷰', nClickCode: 'oused' },
}

export enum ReviewDetailType {
  PHOTO_VIDEO = 'PHOTO_VIDEO',
  BESTPICK = 'BESTPICK',
  GENERAL = 'GENERAL',
  FIRST_WRITTEN_REVIEW = 'FIRST_WRITTEN_REVIEW',
}

type ReviewDetailConfig = {
  [key in ReviewDetailType]?: {
    name: string
    className: string
  }
}

export const ReviewDetail: ReviewDetailConfig = {
  PHOTO_VIDEO: { name: '포토/동영상', className: 'photo' },
  BESTPICK: { name: '스토어PICK', className: 'store_pick' },
  GENERAL: { name: '상품리뷰', className: 'general_review' },
}

export enum StatusType {
  BLIND = 'BLIND',
  DELETE = 'DELETE',
  NORMAL = 'NORMAL',
  REMOVE = 'REMOVE',
}

export interface ReviewPagingParams {
  accountNo?: number
  bestReviewYn?: boolean
  channelNo?: number
  fromDate?: string
  knowledgeShoppingMallProductId?: string
  memberId?: string
  memberIdNo?: string
  memberNo?: number
  merchantNo?: string
  originProductNo?: string
  page: number
  pageSize: number
  productName?: string
  productNos?: string[]
  productOrderNo?: string
  reviewContentClassTypes?: ReviewContentClassType[]
  reviewDisplayStatusTypes?: StatusType[]
  reviewScore?: number
  reviewSearchSortTypes?: ReviewSearchSortType[]
  reviewServiceType?: ReviewServiceType
  reviewType?: ReviewType
  selectReportYn?: boolean
  sortType?: ReviewSearchSortType
  toDate?: string
  topicCode?: string
  withPrice?: string // smartstore api 의 parameter X
  isMaskedNaverId?: string
}

export interface ReviewIds {
  ids: number[]
  totalCount: number
}

export interface RepReviewAttachment {
  attachCount: number
  reviewAttachResult: ReviewAttachment
  reviewId: number
}

export interface WritableReviews {
  writableReviews: WritableReviewsContent[]
  totalCount: number
  page?: number
}

export interface WritableReviewsContent {
  orderId: string
  productName: string
  productImageUrl: string
  productOrderId: string
  productUrl: string
  filteredOptionContents: string
  serviceType: string
  merchantChannelServiceType: string
  merchantServiceType: string
  merchantUrl: string
  merchantName: string
  merchantNo: string
  merchantChannelNo: string
  merchantChannelName: string
  merchantChannelUrl: string
  purchaseDecisionYmdt: number
  reviewCreateYmdt: string
  productOrderBenefitInfos: ProductOrderBenefitInfo[]
}

/**
 * 리뷰 작성(수정) Form (사용자 입력)
 */
export interface ReviewUserInputForm {
  id?: number // Long	리뷰 ID
  orderNo?: string // 페이 주문번호
  productOrderNo?: string // 페이 상품주문번호
  reviewType: ReviewType // 타입(일반/한달사용)
  reviewScore: number // 별점
  reviewAttaches?: ReviewAttachmentForm[] // 리뷰 첨부
  reviewEvaluationValueSeqs: number[] // 평가항목값 id Set
  reviewContent?: string // 내용
  productOptionContentNoDisplay?: boolean // 리뷰 옵션 비노출 여부
}

/**
 * 리뷰 작성 API (POST /reviews) model
 *  - http://dev.api.sell.navercorp.com/#/reviews/createReviewUsingPOST_4
 */
export interface ReviewForm extends ReviewUserInputForm {
  channelNo: number // 채널번호
  channelServiceType: string // 채널 서비스 타입 [ STOREFARM, WINDOW, AFFILIATE ]
  checkoutMerchantId: string // 페이 가맹점 아이디
  checkoutMerchantNo: number // integer($int64)  페이 가맹점 번호
  detailCategorizeCategoryId?: string // 세분류 카테고리ID
  hostingBusinessSiteSeq?: number // integer($int64) 호스팅사 일련번호
  knowledgeShoppingMallProductId?: string // 지식쇼핑 가맹점 상품 아이디
  largeCategorizeCategoryId?: string // 대분류 카테고리ID
  leafCategoryId: string // 리프 카테고리 ID
  middleCategorizeCategoryId?: string // 중분류 카테고리ID
  nationCode?: string // example: KR  리뷰 작성 국가코드
  originProductNo: number // 원상품번호
  productName: string // 페이 상품명
  productNo: string // 상품번호
  productOptionContent: string
  productUrl?: string // 페이 상품URL
  purchaseDecideDate: string // 구매확정일시
  productOrderAmount: string // 상품주문금액
  productPaymentAmount: string // 상품결제금액
  reviewContentClassType?: string // 리뷰 내용 유형 [ TEXT, PHOTO, VIDEO ]
  reviewPersonalizeInfoVO?: ReviewPersonalizeSize
  reviewServiceType: string // 리뷰 서비스 타입 [ CHECKOUT, SELLBLOG ]
  smallCategorizeCategoryId?: string // 소분류 카테고리ID
  writeLocationType?: string // 리뷰 작성위치 [ MOBILE, PC ]
  writerMemberId: string // 네이버 아이디
  writerMemberIdNo: string // 네이버 아이디 번호
  writerMemberNo: number // 작성자 페이회원번호
}

export interface ReviewPersonalizeSize {
  naverIdNo: string
  agreeYn: boolean
  bottoms: number
  height: number
  shoe: number
  tops: string
  weight: number
}

// 리뷰 작성 완료 응답
export interface ReviewRegistrationResult {
  reviewId: string // 리뷰 ID
  reviewPayBenefitResponseVO?: {
    actualAccumulatedAmount: number
    isSuccess: boolean
  }
  isAddMyInfoAvailable?: boolean // 내 정보 추가 가능여부
  mySizeTarget?: MySizeTarget
  result: ReviewUserInputForm
}

// 리뷰 수정 완료 응답
export interface ReviewModificationResult extends Review {
  message?: string
}

export interface MySizeTarget {
  reviewId: string // 리뷰 ID
  largeCategorizeCategoryId?: string // 대분류 카테고리ID
  middleCategorizeCategoryId?: string // 중분류 카테고리ID
  smallCategorizeCategoryId?: string // 소분류 카테고리ID
  detailCategorizeCategoryId?: string // 세분류 카테고리ID
  leafCategoryId: string // 리프 카테고리 ID
}

// 리뷰 작성(수정) 대상 상품 구성요소
export interface ReviewProduct extends ReviewUserInputForm {
  productName: string
  productOrderNo: string
  productImageUrl: string
  productClassType: ProductClassType // 상품유형코드
  productOrderStatusType: ProductOrderStatusType
  filteredOptionContents: string
  serviceType: OrderServiceType
  merchantName: string
  merchantChannelServiceType: string // STOREFARM, SHOPPINGWINDOW
  saleChannelType: SaleChannelType // 판매채널
  productOrderBenefitInfos: ProductOrderBenefitInfo[] // Order API를 통해 전달되는 혜택 정보
  reviewCreateYmdt: string // 일반리뷰 작성일
  reviewWriteExpirationYmdt: string // 일반리뷰 작성기한 만기일
  afterUseReviewCreateYmdt: string // 한달리뷰 작성일
  afterUseReviewWriteExpirationYmdt: string // 한달리뷰 작성기한 만기일
  userId?: string // 회원 아이디
  userNickname?: string // 회원 닉네임
  evaluations: ReviewEvaluation[]
  reviewContentClassType?: ReviewContentClassType
  channelServiceType?: ChannelServiceType
  reviewServiceType?: ReviewServiceType
  subVertical?: SubVertical
  merchantChannelNo?: string
}

// 리뷰 작성 폼 State
export interface RegisterReviewState {
  orderNo: string
  reviewProducts: ReviewProduct[]
  selectedProductOrderNos: string[]
  currentReviewProduct: ReviewProduct
  nextReviewProduct: ReviewProduct
  mySizeTargets: MySizeTarget[]
  result: ReviewUserInputForm
  errorMessage?: string
}

// 리뷰 수정 Redux Store State
export interface ModifyReviewState {
  result: Review
  hasResultUpdated: boolean
}

export namespace ReviewUtil {
  export const getThumbNailImageType = (reviewAttachmentType: ReviewAttachmentType) => {
    return reviewAttachmentType === ReviewAttachmentType.M ? THUMB_TYPE.FFN300_300 : THUMB_TYPE.F300_300
  }
  export const getFormattedAverageScore = (averageReviewScore: number) => {
    return (Math.round(averageReviewScore * 10) / 10.0).toFixed(1)
  }
  export const shownReview = (product: Product) => {
    return product.purchaseReviewInfo && product.purchaseReviewInfo.purchaseReviewExposure && product.payExposure
  }
  export const getDisplayTotalCount = totalReviewCount => {
    return totalReviewCount > 999 ? '999+' : totalReviewCount
  }
  export const getCommentWriterImageUrl = (product: Product) => {
    return (
      product.windowProduct &&
      product.windowProduct.channel &&
      product.windowProduct.channel.logoImage &&
      product.windowProduct.channel.logoImage.imageUrl
    )
  }

  export const getEditedReviewContent = (
    reviewContent: string,
    reviewTopics: ReviewTopic[],
    topicClassName: string,
  ) => {
    if (ArrayUtils.isNotEmpty(reviewTopics)) {
      return (
        <>
          {reviewTopics.map((reviewTopic, idx) => {
            const { startIdx, endIdx, invalidTopic } = reviewTopic
            if (invalidTopic) {
              return <span key={idx}>{HtmlUtils.decodeHtmlContext(reviewContent)}</span>
            }
            const prevEndNo = idx > 0 ? reviewTopics[idx - 1].endIdx + 1 : 0
            return (
              <span key={idx}>
                {HtmlUtils.decodeHtmlContext(reviewContent.substring(prevEndNo, startIdx))}
                <em className={topicClassName}>
                  {HtmlUtils.decodeHtmlContext(reviewContent.substring(startIdx, endIdx + 1))}
                </em>
                {idx === reviewTopics.length - 1 && HtmlUtils.decodeHtmlContext(reviewContent.substring(endIdx + 1))}
              </span>
            )
          })}
        </>
      )
    }
    return HtmlUtils.decodeHtmlContext(reviewContent)
  }

  export const getReviewsSortedByIds = (reviews: Review[], sortedReviewIds: string[]) => {
    if (
      ArrayUtils.isEmpty(sortedReviewIds) ||
      (ArrayUtils.isNotEmpty(sortedReviewIds) && sortedReviewIds.length === 1)
    ) {
      return reviews
    }
    const originReviewIds = reviews.map(review => review.id)
    const originSortedIdxes = []
    originReviewIds.forEach((reviewId, idx) => {
      originSortedIdxes.push({ origin: idx, sorted: sortedReviewIds.indexOf(String(reviewId)) })
    })
    const sortedReviews = []
    originSortedIdxes.forEach(idxes => (sortedReviews[idxes.sorted] = reviews[idxes.origin]))
    return sortedReviews.filter(review => review) // sortedReviewIds 에 포함된 reviewId 가 originReviewIds 에는 포함되지 않을 수 있음
  }

  export const getReviewAttachesSortedByIds = (reviewAttaches: RepReviewAttachment[], sortedReviewIds: string[]) => {
    if (
      ArrayUtils.isEmpty(sortedReviewIds) ||
      (ArrayUtils.isNotEmpty(sortedReviewIds) && sortedReviewIds.length === 1)
    ) {
      return reviewAttaches
    }
    const originReviewIds = reviewAttaches.map(attach => attach.reviewId)
    const originSortedIdxes = []
    originReviewIds.forEach((reviewId, idx) => {
      originSortedIdxes.push({ origin: idx, sorted: sortedReviewIds.indexOf(String(reviewId)) })
    })
    const sortedReviewAttaches = []
    originSortedIdxes.forEach(idxes => (sortedReviewAttaches[idxes.sorted] = reviewAttaches[idxes.origin]))
    return sortedReviewAttaches.filter(review => review) // sortedReviewIds 에 포함된 reviewId 가 originReviewIds 에는 포함되지 않을 수 있음
  }

  export const filterNormalComments = (reviewComments: ReviewComment[]) => {
    return reviewComments.filter(review => review.reviewCommentDisplayStatusType === StatusType.NORMAL)
  }

  export const getOgTagImgPath = (attachPath: string, reviewAttachmentType: ReviewAttachmentType) => {
    if (attachPath && reviewAttachmentType) {
      return `${attachPath}?type=${getThumbNailImageType(reviewAttachmentType)}`
    }
    return process.env.PROJECT_NAME === 'mobile'
      ? 'https://ssl.pstatic.net/static.checkout/stylewindow/m/img/no_img.png'
      : 'https://ssl.pstatic.net/static.checkout/stylewindow/p/img/no_img.png'
  }

  export const getReviewSetProfileImage = (profileImages: NaverProfileImage[], originReview: Review) => {
    const review = { ...originReview }
    if (ArrayUtils.isNotEmpty(profileImages)) {
      const profileImage = profileImages.find(profileImage => profileImage.idNo === review.writerMemberIdNo)
      if (!profileImage.imageUrl) {
        review.writerMemberProfileImageUrl = DEFAULT_PROFILE_IMG_URL
      }
    } else if (isDev) {
      // review.writerMemberProfileImageUrl - 스스플에서 내려주는 회원id 로 패턴화한 회원 프로필 이미지 url
      // 회원이 프로필 이미지를 등록하지 않은 경우, 해당 이미지 url 로 포토 인프라 요청 시 404 error 발생 => 포토인프라에서 404 error 처리 부하가 커서, 가급적 요청하지 말아달라고 함
      // 로컬에서는 회원 api 를 통해 프로필 이미지를 조회하지 않으므로, 회원id 로 패턴화한 회원 프로필 이미지 url 를 사용해야 하나, 포토인프라 부하 저하를 위해 writerMemberProfileImageUrl 를 '' 로 설정
      review.writerMemberProfileImageUrl = ''
    }
    return review
  }

  export const getReviewMaskedNaverId = (originReview: Review): Review => {
    const review = { ...originReview }
    review.writerMemberId = getMaskedNaverId(review.writerMemberId)
    if (ArrayUtils.isNotEmpty(review.reviewComments)) {
      review.reviewComments.forEach(comment => {
        comment.writerMerchantId = getMaskedNaverId(comment.writerMerchantId)
      })
    }
    return review
  }

  export const removeUnnecessaryFields = (originReview: Review, isMyReview?: boolean): Review => {
    const review = { ...originReview }
    review.checkoutMerchantId = ''
    review.checkoutMerchantNo = 0
    review.merchantNo = ''
    review.merchantName = ''
    if (ArrayUtils.isNotEmpty(review.reviewComments)) {
      review.reviewComments.forEach(comment => {
        comment.writerMerchantNo = 0
      })
    }
    if (!isMyReview) {
      review.writerMemberIdNo = ''
      review.writerMemberNo = 0
      review.orderNo = ''
      review.productOrderNo = ''
    }
    return review
  }
}

export namespace ReviewNclickUtil {
  export const getReviewListCode = (reviewAttaches: ReviewAttachment[]): string => {
    if (ArrayUtils.isNotEmpty(reviewAttaches)) {
      if (reviewAttaches[0].reviewAttachmentType === ReviewAttachmentType.M) {
        return 'listvid'
      }
      return 'listpt'
    }
    return 'listtext'
  }
}

export interface ReviewImageInspectionResult {
  type: ImageInspectionType
  image: ReviewAttachmentForm
}
